import { Component } from 'react';
import * as Sentry from '@sentry/react';

// Matches the error thrown when a client with a stale bundle tries to lazy-load
// a view whose chunk hash changed in a newer deploy.
const STALE_CHUNK_RE = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

// A plain reload isn't enough for the installed PWA case: the still-active service worker
// from the old deploy precaches the app shell (index.html) itself, so a bare
// window.location.reload() can be served entirely from that stale precache instead of
// hitting the network — reproducing the exact same stale chunk error a second time, at
// which point the once-per-session guard above gives up and leaves the user stuck on the
// static "Refresh" screen forever. Unregistering the worker and clearing its caches first
// removes anything that could intercept the reload, guaranteeing it fetches the current
// deploy's app shell over the network.
function recoverFromStaleChunk() {
  const reload = () => window.location.reload();
  // Don't let a hung unregister/cache-clear promise strand the user on the error screen.
  const timeout = setTimeout(reload, 2000);
  Promise.resolve()
    .then(() => ('serviceWorker' in navigator ? navigator.serviceWorker.getRegistrations() : []))
    .then(regs => Promise.all(regs.map(r => r.unregister())))
    .then(() => ('caches' in window ? caches.keys() : []))
    .then(keys => Promise.all(keys.map(k => caches.delete(k))))
    .then(() => { clearTimeout(timeout); reload(); })
    .catch(() => { clearTimeout(timeout); reload(); });
}

export class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (STALE_CHUNK_RE.test(error?.message || '')) {
      // Reload once per tab session rather than looping forever if the deploy is genuinely broken.
      if (!sessionStorage.getItem('mr_stale_chunk_reload')) {
        sessionStorage.setItem('mr_stale_chunk_reload', '1');
        recoverFromStaleChunk();
        return;
      }
    }
    Sentry.captureException(error, { extra: info });
  }

  render() {
    if (this.state.error) {
      const dm = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return (
        <div style={{
          minHeight: '100vh', background: dm ? '#080c10' : '#f0f4f8', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 16, padding: 24, fontFamily: 'monospace',
        }}>
          <div style={{ fontSize: 28, color: '#e84040' }}>⚠</div>
          <div style={{ fontSize: 14, color: dm ? '#c0d0d8' : '#1a2332', textAlign: 'center', maxWidth: 320, lineHeight: 1.6 }}>
            Something went wrong. Please refresh the page.
          </div>
          {import.meta.env.DEV && (
            <pre style={{
              fontSize: 10, color: '#e87030', maxWidth: 500, width: '100%',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all', textAlign: 'left',
              background: dm ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)', padding: 12, borderRadius: 8,
              border: '1px solid rgba(232,112,48,0.3)', overflowY: 'auto', maxHeight: 300,
            }}>
              {this.state.error.toString()}{'\n\n'}{this.state.error.stack}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', background: dm ? 'rgba(0,216,240,0.1)' : 'rgba(0,112,168,0.1)',
              border: `1px solid ${dm ? 'rgba(0,216,240,0.3)' : 'rgba(0,112,168,0.3)'}`, borderRadius: 20,
              color: dm ? '#00d8f0' : '#0070a8', cursor: 'pointer', fontSize: 12, fontFamily: 'monospace',
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
